import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Twitter, 
  Facebook, 
  Instagram, 
  Youtube,
  Mail,
  MapPin,
  ExternalLink,
  Clock,
  Globe
} from 'lucide-react';
import { useSiteConfig } from '../../hooks/useSiteConfig';

const Footer: React.FC = () => {
  const siteConfig = useSiteConfig();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Championships', path: '/championships' },
    { name: 'Teams', path: '/teams' },
    { name: 'Seasons', path: '/seasons' },
    { name: 'Standings', path: '/standings' },
    { name: 'Scores', path: '/scores' },
    { name: 'Players', path: '/players' },
    { name: 'Coaches', path: '/coaches' },
  ];

  const socialLinks = [
    { name: 'Twitter', icon: Twitter, url: '#' },
    { name: 'Facebook', icon: Facebook, url: '#' },
    { name: 'Instagram', icon: Instagram, url: '#' },
    { name: 'YouTube', icon: Youtube, url: '#' },
  ];

  return (
    <footer className="relative mt-20 border-t border-gray-800">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-7 gap-6 lg:gap-8 mb-12">
          {/* Brand Section */}
          <div className="col-span-1 lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <Trophy className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold text-white">{siteConfig.name}</h3>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              {siteConfig.description || 'Comprehensive championship data and analytics for sports enthusiasts.'}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3 mb-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    className="w-10 h-10 rounded-lg bg-gray-800/50 flex items-center justify-center hover:bg-primary/20 transition-colors"
                    aria-label={social.name}
                  >
                    <Icon className="w-5 h-5 text-gray-400 hover:text-primary" />
                  </a>
                );
              })}
            </div>
            
            {/* Network Affiliation Card */}
            <a
              href="https://www.the-sports-machine.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-6 p-4 rounded-lg border-2 border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 transition-all transform hover:scale-105 hover:shadow-lg"
              style={{
                borderColor: `${siteConfig.theme?.primary?.[500] || '#3B82F6'}33`,
                background: `linear-gradient(135deg, ${siteConfig.theme?.primary?.[500] || '#3B82F6'}1A 0%, ${siteConfig.theme?.primary?.[500] || '#3B82F6'}0D 100%)`
              }}
            >
              <div className="flex items-center space-x-3">
                <Trophy className="w-8 h-8 text-primary flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">A Proud Affiliate of</p>
                  <p className="text-sm font-bold text-white">The Sports Machine Network</p>
                </div>
              </div>
            </a>
          </div>

          {/* Quick Links, Professional, Collegiate, High School - grouped in flex container */}
          <div className="col-span-1 lg:col-span-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-8">
              {/* Quick Links */}
              <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Quick Links</h4>
            <ul className="space-y-1.5">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary text-xs transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
              </div>

              {/* Professional Sports */}
              <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Professional</h4>
            <ul className="space-y-1.5">
              {[
                { name: 'The Football Machine', url: 'https://www.the-football-machine.com', sport: 'football' },
                { name: 'The Basketball Machine', url: 'https://www.the-basketball-machine.com', sport: 'basketball' },
                { name: 'The Baseball Machine', url: 'https://www.the-baseball-machine.com', sport: 'baseball' },
                { name: 'The Hockey Machine', url: 'https://www.the-hockey-machine.com', sport: 'hockey' },
              ].map((site) => (
                <li key={site.name}>
                  <a
                    href={site.url}
                    className={`text-xs transition-colors flex items-center space-x-1 ${
                      site.sport === siteConfig.sport && siteConfig.level !== 'college'
                        ? 'text-primary font-semibold'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <span>{site.name}</span>
                    {site.url !== window.location.origin && (
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
              </div>

              {/* Collegiate Sports */}
              <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Collegiate</h4>
            <ul className="space-y-1.5">
              {[
                { name: 'The College Football Machine', url: 'https://www.the-college-football-machine.com', sport: 'football', level: 'college' },
                { name: 'The College Basketball Machine', url: 'https://www.the-college-basketball-machine.com', sport: 'basketball', level: 'college' },
                { name: 'The College Baseball Machine', url: 'https://www.the-college-baseball-machine.com', sport: 'baseball', level: 'college' },
                { name: 'The College Hockey Machine', url: 'https://www.the-college-hockey-machine.com', sport: 'hockey', level: 'college' },
              ].map((site) => (
                <li key={site.name}>
                  <a
                    href={site.url}
                    className={`text-xs transition-colors flex items-center space-x-1 ${
                      site.sport === siteConfig.sport && siteConfig.level === 'college'
                        ? 'text-primary font-semibold'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <span>{site.name}</span>
                    {site.url !== window.location.origin && (
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
              </div>

              {/* High School Sports */}
              <div>
            <h4 className="text-white font-semibold mb-3 text-sm">High School</h4>
            <ul className="space-y-1.5">
              {[
                { name: 'USA Prep Football', url: 'https://www.usaprepfootball.com' },
                { name: 'California Prep Football', url: 'https://www.californiaprepfootball.com' },
                { name: 'Florida Prep Football', url: 'https://www.flprepfootball.com' },
                { name: 'Texas Prep Football', url: 'https://www.txprepfootball.com' },
              ].map((site) => (
                <li key={site.name}>
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs transition-colors flex items-center space-x-1 text-gray-400 hover:text-white"
                  >
                    <span>{site.name}</span>
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                </li>
              ))}
            </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>info@the-sports-machine.com</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4" />
              <span>Overland Park, KS</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>24/7 Live Scores</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4" />
              <span>Covering All Sports</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-500">
              © {currentYear} {siteConfig.name}. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
              <Link to="/cookies" className="hover:text-primary transition-colors">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;